import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { auditTime, distinctUntilChanged, filter, map, Subject, Subscription, takeUntil } from 'rxjs';
import { selectAppState, selectStudy, selectReportContentItemsSlice, selectIsStudySaved } from '../../state/selectors';
import { ReportContentItemResponse, Sample, FileMetaData } from '../../../shared/models/run.model';
import { setPanel, setRunSetup } from '../../state/actions';
import { Study } from '../../../shared/models/study.model';
import { MatSelectChange } from '@angular/material/select';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { pageSizeOptions } from '../../../shared/constants/app.const';
import { CalLot } from '../../../shared/models/cal-lot.model';
import { QcLot } from '../../../shared/models/qc-lot.model';
import { ReplicatesToRun } from '../../../shared/models/assays.model';
import { getUniqueAccessions } from '../../../shared/utils/helper';
import { isInternalQcBeadRegion } from '../../../shared/utils/run-info.utils';
import { isMatchKitRunType, toReferenceWorkflow } from '../../../shared/utils/run-type.util';
import { CalculationResult } from '../../../shared/models/result.model';
import { RunTabChange } from '../../../shared/models/run-tab-change.model';
import { ColumnSelection } from '../../../shared/models/dialog.model';

@Component({
  selector: 'app-run-setup',
  templateUrl: './run-setup.component.html',
  styleUrl: './run-setup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RunSetupComponent implements OnInit, OnDestroy {
  isStudySaved$ = this.store.select(selectIsStudySaved);
  isPrintView = false;
  isPrintMode = false;
  private tableResizeObserver?: ResizeObserver;

  /** Keeps the Sample ID column's width cap at twice the Accession column's measured width. */
  @ViewChild('runSamplesTable', { read: ElementRef }) set runSamplesTable(table: ElementRef<HTMLTableElement> | undefined) {
    this.tableResizeObserver?.disconnect();
    this.tableResizeObserver = undefined;
    if (!table || typeof ResizeObserver === 'undefined') {
      return;
    }
    const element = table.nativeElement;
    const apply = () => {
      const accession = element.querySelector<HTMLElement>('th.mat-column-Accession');
      if (accession && accession.offsetWidth > 0) {
        element.style.setProperty('--sample-id-max', `${accession.offsetWidth * 2}px`);
      }
    };
    this.tableResizeObserver = new ResizeObserver(apply);
    this.tableResizeObserver.observe(element);
    apply();
  }
  @Input() ownTabIndex = 0;
  // *MS Similar to the other Run Info tabs, we aggressively pause this component while hidden. Run Setup rebuilds large
  //   tables, so detaching change detection during tab switches and caching pending study updates prevents those
  //   expensive rebuilds from firing while the user is editing another tab.*MS
  @Input() set tabChange(tabChangeEvent: RunTabChange | null) {
    if (!tabChangeEvent) {
      return;
    }

    const becameInactive = tabChangeEvent.previousIndex === this.ownTabIndex &&
      tabChangeEvent.currentIndex !== this.ownTabIndex;
    const becameActive = tabChangeEvent.currentIndex === this.ownTabIndex;

    if (becameInactive && !this.isUpdated) {
      this.scheduleDeferredSave(() => this.storeFormValues());
    }
    if (becameInactive) {
      this.isTabActive = false;
      this.cancelScheduledTabActivation();
      // *MS Detach change detection while the tab is hidden so Angular skips this tree during other tab updates.*MS
      this.cdr.detach();
      return;
    }

    if (becameActive) {
      this.scheduleTabActivation();
    }
  }

  selectedStudyData$ = this.store.select(selectStudy);
  appState$ = this.store.select(selectAppState);
  selectedStudyData!: Study | null;
  panelName = '';
  studyName = '';
  specVersion = '';
  appVersion = '';
  isProcessing = false;
  // *MS Track the freshest Report Items payload via selector so Run Setup never dispatches stale grid data back to NgRx.*MS
  private latestReportContentResponse: ReportContentItemResponse | null = null;
  runSamples: Sample[] = [];
  replicatesToRun: ReplicatesToRun[] | undefined;
  isLoading = false;
  isUpdated = true;
  isEmpty = false;
  existingStudy = false;
  runSetupLength = 0;
  pageSize = pageSizeOptions[0];
  pageIndex = 0;
  pageSizes = pageSizeOptions;
  accessionsData: string[] = [];
  calLotData: CalLot[] = [];
  qcLotData: QcLot[] = [];
  phaseType!: string;
  testSpecs: string[] = ['RMQC', 'PreBulk', 'Matched Kit', 'Stability', 'Adjustment'];
  selectedTestSpec!: string;
  /** Test spec a run type defaults to, used only until the study records one of its own. */
  private readonly runTypeTestSpecMap: Record<string, string> = {
    'RMQC': 'RMQC',
    'PreBulk': 'PreBulk',
    'Matched Kit': 'Matched Kit',
    'ControlMatchKit': 'Matched Kit',
    'CalibratorMatchKit': 'Matched Kit',
    'Stability': 'Stability',
    'Control Assignment': 'Adjustment',
    'ControlAssignment': 'Adjustment',
    'Calibrator Assignment': 'Adjustment',
    'CalibratorAssignment': 'Adjustment',
    'Calibrator Adjustment': 'Adjustment',
    'CalibratorAdjustment': 'Adjustment'
  };
  /**
   * Spellings a recorded test spec can arrive in, keyed by comparison key. Saved studies come back
   * with the API spelling, which is not one of the selectable labels.
   */
  private readonly testSpecAliases: Record<string, string> = {
    'matchkit': 'Matched Kit',
    'controlmatchkit': 'Matched Kit',
    'calibratormatchkit': 'Matched Kit',
    'controlassignment': 'Adjustment',
    'calibratorassignment': 'Adjustment',
    'calibratoradjustment': 'Adjustment'
  };
  assignSpecs: string[] = ['MiniBlend', 'Formulation', 'Final'];
  selectedAssignSpec: string | null = null;
  selectedKitLot!: string;
  kitLots: string[] = [];
  runSetupFormGroup!: FormGroup;
  sampleTypes: string[] = ['Patient', 'Cal - Cal', 'Cal - Test', 'Control - QC', 'Control - Test', 'Other', 'N/A'];
  private readonly baseRunSamplesColumns: string[] = ['Accession', 'Sample ID', 'Sample Type', 'Req Num Replicates'];
  runSamplesColumns: string[] = [...this.baseRunSamplesColumns];
  displayedRunSamplesColumns: string[] = [...this.baseRunSamplesColumns];
  reagentLotColumns: string[] = [];
  /** Instrument + lot for each column in `reagentLotColumns`, keyed by that same column id. */
  reagentLotColumnMeta: Record<string, { lot: string; instrumentId: string } | undefined> = {};
  tableColumns: ColumnSelection[] = [];
  selectedColumns: ColumnSelection[] = [];
  reagentLotValues: Record<string, Record<string, number>> = {};
  runSamplesDataSource!: MatTableDataSource<Sample>;
  runSamplesFormArray!: FormArray<FormGroup>;
  // Tracks the last processed run samples reference to skip redundant rebuilds of the same data set.
  private lastProcessedRunSamplesRef: Sample[] | null = null;
  private sampleIdByAccession = new Map<string, string>();
  // Handles the throttled valueChanges subscription so we can unsubscribe cleanly on destroy.
  private runSamplesValueChangesSubscription?: Subscription;
  private isTabActive = false;
  private pendingSelectedStudy: Study | null = null;
  private tabActivationHandle: number | ReturnType<typeof setTimeout> | null = null;
  private tabActivationHandleIsTimeout = false;
  private navContainer: HTMLElement | null = null;
  private readonly navContainerClass = 'nav-container--run-setup';
  private destroyed = false;
  protected destroy$ = new Subject<boolean>();
  // Any field setter pushes here; storeFormValues() used to require a manual "Update Study" click.
  private formChanges$ = new Subject<void>();
  displayedColumnsMissing: string[] = ['Sample ID'];
  calculationResult!: CalculationResult | null | undefined;
  missingSamples!: string[];
  studyComment: string = '';
  private lastKnownComment = '';
  calculateAverages = false;
  useLegacy4pl = false;
  optimizeAdjustment = false;
  skipMissingSamples = false;

  constructor(
    private store: Store,
    private fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly renderer: Renderer2,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.cdr.detach();
  }

  ngOnInit(): void {
    this.isPrintView = this.router?.url?.includes('print');
    if (this.isPrintView) {
      this.isTabActive = true;
      this.cdr.reattach();
      // The print route is print mode; there is no other reason to be on it. This used to be
      // switched on by the print menu opening over the page.
      this.isPrintMode = true;
    }
    this.syncPrintColumns();
    this.setNavContainerOverride(true);
    this.appState$.pipe(
      filter(appState => !!appState),
      takeUntil(this.destroy$)
    ).subscribe(appState => {
      this.existingStudy = appState.existingStudy;
      this.panelName = appState.panelName ?? '';
      this.appVersion = appState.appVersion ?? '';
      this.isProcessing = !!appState.isProcessing;
    });

    this.selectedStudyData$.pipe(
      filter(selectedStudy => !!selectedStudy),
      distinctUntilChanged((previous, current) =>
        // *MS Only react when the study identity or the runSamples reference actually changes to avoid redundant rebuilds.*MS
        previous?.studyId === current?.studyId &&
        previous?.runSamples === current?.runSamples
      ),
      takeUntil(this.destroy$)
    ).subscribe(selectedStudy => {
      if (!this.isTabActive) {
        // *MS Cache the study update and skip heavy work until the tab becomes visible.*MS
        this.pendingSelectedStudy = selectedStudy;
        return;
      }
      this.applySelectedStudy(selectedStudy);
    });

    this.store.select(selectStudy).pipe(
      map(selectedStudy => selectedStudy?.comments ?? ''),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(comments => {
      const previous = this.lastKnownComment;
      this.lastKnownComment = comments;
      if (this.studyComment === previous) {
        this.studyComment = comments;
        if (this.isTabActive) {
          this.cdr.markForCheck();
        }
      }
    });

    // *MS Subscribe to the dedicated Report Items slice; reference-only comparer keeps perf wins but ensures we see true updates.*MS
    this.store.select(selectReportContentItemsSlice).pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(reportContent => {
      // *MS Mirror the latest Report Items payload so Run Setup never dispatches stale rows back into NgRx.*MS
      this.latestReportContentResponse = reportContent ?? null;
    });

    this.formChanges$.pipe(
      auditTime(300),
      takeUntil(this.destroy$)
    ).subscribe(() => this.storeFormValues());
  }

  /** Marks a field dirty and queues an auto-save, replacing the old "Update Study" button click. */
  private markDirty(): void {
    this.isUpdated = false;
    this.formChanges$.next();
  }

  loadTables(study: Study): void {
    if (this.lastProcessedRunSamplesRef === study.runSamples) {
      // *MS Skip costly table reconstruction when the incoming runSamples array is the same reference we've already processed.
      //   Earlier versions rebuilt every FormArray row on any NgRx tick, even if the payload was unchanged, which caused
      //   noticeable freezes while navigating between tabs.*MS
      return;
    }

    this.runSamples = study.runSamples;
    const accessions = getUniqueAccessions(
      study.patientFileMetadata.concat(study.calFileMetadata).concat(study.controlFileMetadata)
    );
    if (accessions?.length && Array.isArray(accessions)) {
      this.accessionsData = [...accessions].sort((a, b) => a.localeCompare(b));
    }

    if (!this.existingStudy) {
      this.replicatesToRun = study?.panelSpecs?.numReplicatesToRun;
      if (study.runSamples.length) {
        // *MS An accession with a zero replicate count across every selected reagent lot has nothing to run;
        //   drop it here too so a later lot reselection removes rows it had previously added.*MS
        this.runSamples = this.runSamples.filter(sample => !this.shouldForceNoReagents(sample.accessionId));
        const accessionNotInRunSamples = this.findMissingAccessions(this.accessionsData, this.runSamples)
          .filter(accession => !this.shouldForceNoReagents(accession));
        if (accessionNotInRunSamples.length) {
          accessionNotInRunSamples.forEach((accession: string) => {
            this.setRunSamplesData(accession, this.replicatesToRun);
          });
          this.runSamples.sort((a, b) => a.accessionId.localeCompare(b.accessionId));
        }
      } else {
        this.sampleIdTypeDataFunction();
      }
    }

    if (this.runSamples?.length > 0 || this.accessionsData?.length > 0) {
      this.runSetupLength = this.runSamples.length;
      this.buildForm(this.runSamples);
      this.isLoading = false;
      this.isEmpty = false;
    } else {
      this.isEmpty = true;
    }

    // *MS Record the processed runSamples reference so future identical emissions can be ignored efficiently.*MS
    this.lastProcessedRunSamplesRef = study.runSamples;
  }

  getUniqueAccessionNumbersFromSamples(): string[] {
    const accessionSet = new Set<string>();

    this.calculationResult?.runStatus?.forEach(report => {
      report.missingSamples?.forEach(sample => {
        accessionSet.add(sample.accessionNo);
      });
    });

    return Array.from(accessionSet).sort();
  }

  findMissingAccessions(accessionList: string[], runSamples: { accessionId: string }[]): string[] {
    const existingAccessions = new Set(runSamples.map(sample => sample.accessionId.trim().toUpperCase()));
    return accessionList.filter(accession => !existingAccessions.has(accession.trim().toUpperCase()));
  }

  sampleIdTypeDataFunction(): void {
    this.runSamples = [];
    this.accessionsData.forEach((accession: string) => {
      if (this.shouldForceNoReagents(accession)) {
        // Zero replicates across every selected reagent lot: nothing to run, so leave it off the table.
        return;
      }
      this.setRunSamplesData(accession, this.replicatesToRun);
    });
  }

  private parseAccessionLotInfo(accession: string): { lot: string; suffix: string } | null {
    const trimmed = accession.trim();
    if (!trimmed) {
      return null;
    }

    const normalized = trimmed.replace(/\.(?=L\d{2}(?:_X\d+)?$)/i, '');
    const sixDigitMatch = /(\d{6})L(\d{2})(?:_X\d+)?$/i.exec(normalized);
    if (sixDigitMatch) {
      return { lot: sixDigitMatch[1], suffix: `L${sixDigitMatch[2]}` };
    }

    const fiveCharMatch = /([A-Za-z0-9]{5})L(\d{2})(?:_X\d+)?$/i.exec(normalized);
    if (fiveCharMatch) {
      return { lot: fiveCharMatch[1], suffix: `L${fiveCharMatch[2]}` };
    }

    return null;
  }

  /** Trailing `_x##`/`_X##` suffix from the original accession, e.g. `_x03` on `875876L02_x03`. */
  private getAccessionXSuffix(accession: string): string {
    const match = /_[xX](\d+)$/.exec(accession.trim());
    return match ? `_x${match[1]}` : '';
  }

  setRunSamplesData(accession: string, replicatesToRun: ReplicatesToRun[] | undefined): void {
    const phaseName = this.phaseType;
    const parsedLotInfo = this.parseAccessionLotInfo(accession);
    let sampleId: string | null = null;
    const mappedSampleId = this.sampleIdByAccession.get(accession.trim());
    const isStabilityOrMatchKit = this.isStabilityOrMatchKitRunType();
    const isControlAssignment = this.isControlAssignmentRunType();
    if (!isStabilityOrMatchKit && !isControlAssignment && mappedSampleId) {
      sampleId = mappedSampleId;
    }
    const controlAssignmentOverride = this.getControlAssignmentDefaults(accession);
    const cntSampleId = this.getCntAccessionSampleId(accession);
    if (!sampleId && cntSampleId) {
      sampleId = cntSampleId;
    }

    if (!isStabilityOrMatchKit) {
      if (isControlAssignment) {
        if (!sampleId && controlAssignmentOverride) {
          sampleId = controlAssignmentOverride.sampleId;
        }
      } else if (parsedLotInfo) {
        const parsedLotNumber = parsedLotInfo.lot;
        const matchingCalLot1 = this.calLotData.find((calLotData1) => calLotData1.calLotNumber === parsedLotNumber);
        const matchingQcLot1 = this.qcLotData.find((qcLotData1) => qcLotData1.masterLotNumber === parsedLotNumber);

        if (matchingCalLot1 || accession.toUpperCase().includes('CAL')) {
          const lotId = matchingCalLot1?.calLotNumber ?? this.calLotData[0]?.calLotNumber ?? parsedLotNumber;
          if (lotId) {
            sampleId = `CALIB${lotId}${parsedLotInfo.suffix}`;
          }
        } else if (matchingQcLot1) {
          sampleId = `QC${parsedLotNumber}${parsedLotInfo.suffix}${this.getAccessionXSuffix(accession)}`;
        }
      }

      if (!sampleId && this.isLxxOverrideRunType() && this.isCalibratorAssignmentCalTestAccession(accession)) {
        sampleId = this.buildCalibratorAssignmentCalTestSampleId(accession);
      }
    }

    let defaultSampleType = this.getDefaultSampleTypeFromSampleId(sampleId);
    if (isStabilityOrMatchKit || isControlAssignment) {
      const normalizedAccession = (accession ?? '').trim().toUpperCase();
      if (normalizedAccession.startsWith('CALIB')) {
        defaultSampleType = 'Cal - Cal';
      } else if (normalizedAccession.startsWith('QC')) {
        defaultSampleType = this.resolveControlLotSampleType(parsedLotInfo?.lot, isControlAssignment ? 'N/A' : 'Control - Test');
      } else if (isStabilityOrMatchKit) {
        defaultSampleType = 'Patient';
      }
    }
    let sampleType = defaultSampleType;
    let reqNumReplicatesCount = this.findReplicateRequirement(sampleType, phaseName, replicatesToRun);
    if (controlAssignmentOverride) {
      defaultSampleType = controlAssignmentOverride.sampleType;
      sampleType = defaultSampleType;
      reqNumReplicatesCount = this.findReplicateRequirement(sampleType, phaseName, replicatesToRun);
    }
    if (sampleType === 'N/A') {
      reqNumReplicatesCount = { num_replicates: 0 } as ReplicatesToRun;
    }
    if (this.isCalCalDefaultAccession(accession)) {
      defaultSampleType = 'Cal - Cal';
      sampleType = defaultSampleType;
      reqNumReplicatesCount = { num_replicates: 4 } as ReplicatesToRun;
    }
    if (this.shouldForceNoReagents(accession)) {
      sampleType = 'N/A';
      reqNumReplicatesCount = { num_replicates: 0 } as ReplicatesToRun;
    }

    const runSample = {
      accessionId: accession.trim().toUpperCase(),
      sampleId: sampleId,
      defaultSampleType: defaultSampleType,
      sampleType: sampleType,
      reqNumReplicates: reqNumReplicatesCount?.num_replicates,
      sampleResult: undefined
    } as Sample;
    this.runSamples = [...this.runSamples];
    this.runSamples.push(runSample);
  }

  private buildCalibratorAssignmentCalTestSampleId(accession: string): string | null {
    const trimmed = (accession ?? '').trim();
    if (!trimmed) {
      return null;
    }

    const normalized = trimmed.toUpperCase();
    const calMatch = /^CAL(\d{1,2})\.(\d)/.exec(normalized);
    if (calMatch) {
      const levelNumber = Number(calMatch[1]);
      return `L${levelNumber < 10 ? '0' : ''}${levelNumber}`;
    }

    const lMatch = /(L\d{2})/i.exec(normalized);
    return lMatch ? lMatch[1].toUpperCase() : null;
  }

  private isCalibratorAssignmentCalTestAccession(accession: string): boolean {
    if (!accession) {
      return false;
    }

    const normalized = accession.trim().toUpperCase();
    if (/^CAL\d{1,2}\.\d/.test(normalized)) {
      return true;
    }

    const decimalPattern = /\d+\.\d/;
    const lPattern = /L\d{2}/i;

    return decimalPattern.test(normalized) && lPattern.test(normalized);
  }

  private getControlAssignmentDefaults(accession: string): { sampleId: string; sampleType: string } | null {
    if (!this.isLxxOverrideRunType()) {
      return null;
    }

    const trimmed = (accession ?? '').trim();
    if (!trimmed) {
      return null;
    }

    const ctrlMatch = /^CTRL(\d+)\.(\d+)(?:_x(\d+))?$/i.exec(trimmed);
    if (ctrlMatch) {
      const controlNumber = Number(ctrlMatch[1]);
      if (!Number.isFinite(controlNumber) || controlNumber <= 0) {
        return null;
      }

      const suffix = ctrlMatch[3] ? `_x${ctrlMatch[3]}` : '';
      const sampleId = controlNumber < 10 ? `L0${controlNumber}` : `L${controlNumber}`;
      return { sampleId: `${sampleId}${suffix}`, sampleType: 'Control - Test' };
    }

    const conMatch = /^(?:CONL?|CNT)(\d+)\.(\d+)(?:_x(\d+))?$/i.exec(trimmed);
    if (!conMatch) {
      return null;
    }

    const controlNumber = Number(conMatch[1]);
    if (!Number.isFinite(controlNumber) || controlNumber <= 0) {
      return null;
    }

    const suffix = conMatch[3] ? `_x${conMatch[3]}` : '';
    const sampleId = controlNumber < 10 ? `L0${controlNumber}` : `L${controlNumber}`;
    return { sampleId: `${sampleId}${suffix}`, sampleType: 'Control - Test' };
  }

  private getCntAccessionSampleId(accession: string): string | null {
    if (!this.isLxxOverrideRunType()) {
      return null;
    }

    const trimmed = (accession ?? '').trim();
    if (!trimmed) {
      return null;
    }

    const match = /^(?:CNT|CONL?)(\d+)\.(\d+)(?:_x(\d+))?$/i.exec(trimmed);
    if (!match) {
      return null;
    }

    const controlNumber = Number(match[1]);
    if (!Number.isFinite(controlNumber) || controlNumber <= 0) {
      return null;
    }

    const suffix = match[3] ? `_x${match[3]}` : '';
    const baseId = controlNumber < 10 ? `L0${controlNumber}` : `L${controlNumber}`;
    return `${baseId}${suffix}`;
  }

  private isLxxOverrideRunType(): boolean {
    return this.isCalibratorAssignmentOrAdjustmentRunType() || this.isControlAssignmentRunType();
  }

  private isControlAssignmentRunType(): boolean {
    const rawRunType = this.selectedStudyData?.runType ?? this.phaseType ?? '';
    const normalized = rawRunType.trim().replace(/[\s_-]+/g, '').toLowerCase();
    return normalized === 'controlassignment';
  }

  private isStabilityOrMatchKitRunType(): boolean {
    const rawRunType = this.selectedStudyData?.runType ?? this.phaseType ?? '';
    const normalized = rawRunType.trim().replace(/[\s_-]+/g, '').toLowerCase();
    return normalized === 'stability' || isMatchKitRunType(rawRunType);
  }

  private findReplicateRequirement(
    sampleType: string,
    phaseName: string | undefined,
    replicatesToRun: ReplicatesToRun[] | undefined
  ): ReplicatesToRun | undefined {
    if (!replicatesToRun?.length) {
      return undefined;
    }

    const normalizedSample = (sampleType ?? '').trim().toLowerCase();
    // Reference data specifies a single "Match Kit" block, so a spec row's phase reads MatchKit
    // whichever match kit run type asked for it. Resolve both sides to the workflow the specs are
    // filed under, or a Control Match Kit study matches no row and its replicate count is blank.
    const normalizePhase = (value: string | undefined | null) =>
      toReferenceWorkflow(value).trim().replace(/[\s_-]+/g, '').toLowerCase();
    const normalizedPhase = normalizePhase(phaseName);

    return replicatesToRun.find(runData =>
      (runData.sample_type ?? '').trim().toLowerCase() === normalizedSample &&
      normalizePhase(runData.phase) === normalizedPhase
    );
  }

  private getDefaultSampleTypeFromSampleId(sampleId: string | null): string {
    if (!sampleId) {
      return 'Patient';
    }

    const normalizedSampleId = sampleId.trim().toUpperCase();

    if (/^CALIB.*L\d{2}(?:_X\d+)?$/.test(normalizedSampleId)) {
      return 'Cal - Cal';
    }

    const qcMatch = /^QC([A-Z0-9]+)L\d{2}(?:_X\d+)?$/.exec(normalizedSampleId);
    if (qcMatch) {
      return this.resolveControlLotSampleType(qcMatch[1]);
    }

    if (/^L\d{2}(?:_X\d+)?$/.test(normalizedSampleId)) {
      return 'Cal - Test';
    }

    return 'Patient';
  }

  /**
   * A control lot's sample type is a matter of import order, not run type: whichever control lot
   * was imported first is the reference lot ("Control - QC"), and every control lot imported after
   * it is being tested against that reference ("Control - Test"). Control Assignment passes 'N/A'
   * as the fallback: its Control - Test samples are the CTRL#.# accessions, never another QC lot.
   */
  private resolveControlLotSampleType(
    lotNumber: string | null | undefined,
    otherLotType: 'Control - Test' | 'N/A' = 'Control - Test'
  ): string {
    const firstImportedLot = this.qcLotData?.[0]?.masterLotNumber?.trim().toUpperCase() ?? '';
    const normalizedLot = (lotNumber ?? '').trim().toUpperCase();
    return firstImportedLot && normalizedLot === firstImportedLot ? 'Control - QC' : otherLotType;
  }

  private buildForm(runSamples: Sample[]): void {
    if (!this.runSetupFormGroup) {
      // *MS Reuse the same FormArray instance so Angular is not forced to recreate every control on each rebuild.*MS
      this.initializeRunSetupForm();
    }

    const formArray = this.runSamplesFormArray;

    runSamples.forEach((runSample: Sample, index: number) => {
      const shouldForceNoReagents = this.shouldForceNoReagents(runSample.accessionId);
      const patchedSampleType = shouldForceNoReagents ? 'N/A' : runSample.sampleType;
      const patchedReqNumReplicates = shouldForceNoReagents ? 0 : runSample.reqNumReplicates;
      const existingGroup = formArray.at(index) as FormGroup | null;
      if (existingGroup) {
        // *MS Patch existing rows silently so we preserve the DOM nodes and avoid cascading change detection work.*MS
        existingGroup.patchValue({
          accessionId: runSample.accessionId,
          sampleId: runSample.sampleId,
          sampleType: patchedSampleType,
          defaultSampleType: runSample.defaultSampleType,
          reqNumReplicates: patchedReqNumReplicates
        }, { emitEvent: false });
      } else {
        formArray.push(this.createRunSampleFormGroup({
          ...runSample,
          sampleType: patchedSampleType,
          reqNumReplicates: patchedReqNumReplicates
        }));
      }
    });

    while (formArray.length > runSamples.length) {
      // *MS Remove stale rows without emitting events so incremental updates stay efficient.*MS
      formArray.removeAt(formArray.length - 1, { emitEvent: false });
    }

    if (!this.runSamplesDataSource) {
      this.runSamplesDataSource = new MatTableDataSource<Sample>([]);
    }
    // *MS Keep the data source instance stable to maintain paginator/sorter bindings.*MS
    this.runSamplesDataSource.data = [...runSamples];
  }

  private initializeRunSetupForm(): void {
    this.runSamplesFormArray = this.fb.array<FormGroup>([]);
    this.runSetupFormGroup = this.fb.group({
      runSamples: this.runSamplesFormArray
    });
    this.bindToRunSampleChanges();
  }

  private bindToRunSampleChanges(): void {
    this.runSamplesValueChangesSubscription?.unsubscribe();
    this.runSamplesValueChangesSubscription = this.runSamplesFormArray.valueChanges
      .pipe(auditTime(50))
      .subscribe(() => {
        // *MS Throttle change notifications so a single edit does not trigger global updates for every keystroke.*MS
        this.markDirty();
      });
  }

  private hasRunSetupChanged(payload: { runSamples: Sample[]; reportContentItemResponse: ReportContentItemResponse; comments: string; testSpec: string; assignSpec: string; refLot: string; calculateAverages: boolean; useLegacy4pl: boolean; optimizeAdjustment: boolean; skipMissingSamples: boolean }): boolean {
    const study = this.selectedStudyData;
    if (!study) {
      return true;
    }

    if (!this.areRunSamplesEqual(payload.runSamples, study.runSamples)) {
      return true;
    }

    return (
      payload.comments !== (study.comments ?? '') ||
      payload.testSpec !== study.testSpec ||
      (payload.assignSpec ?? '') !== (study.assignSpec ?? '') ||
      payload.refLot !== study.refKitLot ||
      payload.calculateAverages !== !!study.calculate_averages ||
      payload.useLegacy4pl !== !!study.use_legacy_4pl ||
      payload.optimizeAdjustment !== !!study.optimize_adjustment ||
      payload.skipMissingSamples !== !!study.skip_missing_samples
    );
  }

  private isCalCalDefaultAccession(accession: string): boolean {
    const normalized = (accession ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
    return normalized === 'HPP 20' || normalized === 'HSP 20';
  }

  private areRunSamplesEqual(nextSamples: Sample[], currentSamples: Sample[]): boolean {
    if (!currentSamples || nextSamples.length !== currentSamples.length) {
      return false;
    }

    for (let i = 0; i < nextSamples.length; i++) {
      const next = nextSamples[i];
      const current = currentSamples[i];
      if (
        next.accessionId !== current.accessionId ||
        next.sampleId !== current.sampleId ||
        next.sampleType !== current.sampleType ||
        next.defaultSampleType !== current.defaultSampleType ||
        next.reqNumReplicates !== current.reqNumReplicates
      ) {
        return false;
      }
    }

    return true;
  }

  missingDataSet(): void {
    const kitList = this.calculationResult?.reportContentItems || [];

    const uniqueLots = new Set<string>();
    kitList.forEach(item => {
      if (item?.kitLotNumber) {
        uniqueLots.add(item.kitLotNumber);
      }
    });
    this.displayedColumnsMissing = ['Sample ID', ...Array.from(uniqueLots)];
  }

  getMissingValues(column: string, element: string): string {
    const reportContentItems = this.calculationResult?.reportContentItems ?? [];
    const runStatus = this.calculationResult?.runStatus ?? [];

    const item = reportContentItems.find(r => r.kitLotNumber === column);
    if (!item) {
      return 'OK';
    }

    const reportContentItemId = column + item.BeadRegion;
    const run = runStatus.find(r => r.reportContentItemId === reportContentItemId);

    if (!run || !run.missingSamples) {
      return 'OK';
    }

    const sample = run.missingSamples.find(s => s.accessionNo === element);
    if (!sample || sample.discarded === undefined) {
      return 'OK';
    }

    return sample.discarded ? 'DISCARDED' : 'MISSING';
  }

  private createRunSampleFormGroup(runSample: Sample): FormGroup {
    const formGroup = new FormGroup({
      accessionId: new FormControl(runSample.accessionId, Validators.required),
      sampleId: new FormControl(runSample.sampleId, Validators.required),
      sampleType: new FormControl(runSample.sampleType, Validators.required),
      defaultSampleType: new FormControl(runSample.defaultSampleType, Validators.required),
      reqNumReplicates: new FormControl(runSample.reqNumReplicates, Validators.required),
    });

    formGroup.get('sampleType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateReplicateCountForSampleType(formGroup);
      });

    return formGroup;
  }

  private updateReplicateCountForSampleType(formGroup: FormGroup): void {
    const sampleType = formGroup.get('sampleType')?.value as string | null | undefined;
    const match = this.findReplicateRequirement(sampleType ?? '', this.phaseType, this.replicatesToRun);
    const nextValue = match?.num_replicates ?? null;
    formGroup.get('reqNumReplicates')?.patchValue(nextValue, { emitEvent: false });
  }

  private shouldForceNoReagents(accession: string | null | undefined): boolean {
    if (!accession || this.reagentLotColumns.length === 0) {
      return false;
    }

    const normalizedAccession = accession.trim().toUpperCase();
    let total = 0;
    let hasAnyValue = false;

    this.reagentLotColumns.forEach(lotColumn => {
      const rawValue = this.reagentLotValues[lotColumn]?.[normalizedAccession];
      const numericValue = Number(rawValue);
      if (!Number.isFinite(numericValue)) {
        return;
      }
      hasAnyValue = true;
      total += Math.trunc(numericValue);
    });

    return hasAnyValue && total === 0;
  }

  private getRunSamplesFromForm(): Sample[] {
    const runSamples: Sample[] = [];

    this.runSamplesFormArray?.controls?.forEach((formGroup: FormGroup) => {
      const runSample: Sample = {
        accessionId: formGroup.get('accessionId')?.value,
        sampleId: formGroup.get('sampleId')?.value,
        sampleType: (formGroup.get('sampleType')?.value),
        defaultSampleType: (formGroup.get('defaultSampleType')?.value),
        reqNumReplicates: +(formGroup.get('reqNumReplicates')?.value ?? 0)
      };

      runSamples.push(runSample);
    });

    return runSamples;
  }

  private getReportContentFromStore(): ReportContentItemResponse {
    // *MS Prefer the selector-fed cache, which updates even when Run Setup is detached, to avoid resending old rows.*MS
    if (this.latestReportContentResponse) {
      return this.latestReportContentResponse;
    }

    return this.selectedStudyData?.reportContentItems ?? {
      variableColumns: [],
      reportContentItems: []
    };
  }

  storeFormValues(): void {
    if (this.isEmpty) {
      return;
    }

    const payload = {
      runSamples: this.getRunSamplesFromForm(),
      reportContentItemResponse: this.getReportContentFromStore(),
      comments: this.studyComment,
      testSpec: this.selectedTestSpec,
      assignSpec: this.selectedAssignSpec ?? '',
      refLot: this.selectedKitLot,
      calculateAverages: this.calculateAverages,
      useLegacy4pl: this.useLegacy4pl,
      optimizeAdjustment: this.optimizeAdjustment,
      skipMissingSamples: this.skipMissingSamples
    };

    if (!this.hasRunSetupChanged(payload)) {
      // *MS Skip dispatch when the latest form snapshot still matches NgRx state.*MS
      this.isUpdated = true;
      return;
    }

    this.store.dispatch(setRunSetup(payload));
    this.isUpdated = true;
  }

  handleTestSpecSelectionChange($event: MatSelectChange): void {
    if ($event.value) {
      // Keep the selectable label; the API spelling is applied where the request is built.
      this.selectedTestSpec = $event.value;
      this.markDirty();
    }
  }

  /**
   * Test spec to show for a study: the one recorded on it, falling back to its run type's default
   * only when it has none. Reapplying the run type default on every study emission would discard
   * the operator's selection each time the study slice changes.
   */
  private resolveTestSpec(study: Study): string {
    return this.toTestSpecOption(study.testSpec) || this.runTypeTestSpecMap[study.runType ?? ''] || '';
  }

  /** Matches a recorded test spec to its selectable label, tolerating spacing and API spellings. */
  private toTestSpecOption(testSpec: string | null | undefined): string {
    const comparisonKey = (value: string): string => value.trim().replace(/[\s_-]+/g, '').toLowerCase();
    const key = comparisonKey(testSpec ?? '');
    if (!key) {
      return '';
    }

    return this.testSpecAliases[key] ?? this.testSpecs.find(option => comparisonKey(option) === key) ?? '';
  }

  handleAssignSpecSelectionChange($event: MatSelectChange): void {
    if ($event.value) {
      this.selectedAssignSpec = $event.value;
      this.markDirty();
    }
  }

  handleKitLotSelectionChange($event: MatSelectChange): void {
    const normalizedValue = ($event.value ?? '') as string;
    this.selectedKitLot = normalizedValue;
    this.markDirty();
  }

  handleCalculateAveragesChange(event: MatCheckboxChange): void {
    this.calculateAverages = event.checked;
    this.markDirty();
  }

  handleSkipMissingSamplesChange(event: MatCheckboxChange): void {
    this.skipMissingSamples = event.checked;
    this.markDirty();
  }

  handleUseLegacy4plChange(event: MatCheckboxChange): void {
    this.useLegacy4pl = event.checked;
    this.markDirty();
  }

  handleCommentsChange(value: string): void {
    this.studyComment = value;
    this.markDirty();
  }

  handleOptimizeAdjustmentChange(event: MatCheckboxChange): void {
    this.optimizeAdjustment = event.checked;
    this.markDirty();
  }

  private updateReagentLotColumns(study: Study | null | undefined): void {
    const allMetadata = [
      ...(study?.patientFileMetadata ?? []),
      ...(study?.calFileMetadata ?? []),
      ...(study?.controlFileMetadata ?? [])
    ];

    const { columns, meta } = this.collectReagentLotColumns(allMetadata);
    this.reagentLotColumns = columns;
    this.reagentLotColumnMeta = meta;

    // Every file keeps its own instrumentId and its own replicateCounts, so summing across all of
    // them (rather than only the newest file per type) is what keeps every instrument's column
    // populated — the same lot on two instruments no longer collapses into a single column's total.
    this.reagentLotValues = this.buildReagentLotValueMap(allMetadata);

    this.runSamplesColumns = [
      ...this.baseRunSamplesColumns,
      ...this.reagentLotColumns
    ];
    this.syncPrintColumns();
  }

  /** One column per instrument+lot combination — the same lot from two instruments is not merged. */
  private buildLotColumnKey(instrumentId: string, lot: string): string {
    return `${instrumentId}__${lot}`;
  }

  private collectReagentLotColumns(
    metadata: FileMetaData[]
  ): { columns: string[]; meta: Record<string, { lot: string; instrumentId: string }> } {
    const meta: Record<string, { lot: string; instrumentId: string }> = {};
    const columns: string[] = [];

    metadata.forEach(file => {
      const instrumentId = (file.instrumentId ?? '').toString().trim() || 'Unmapped';
      (file.reagentLots || []).forEach(rawLot => {
        const lot = this.formatReagentLotLabel(String(rawLot));
        if (!lot) {
          return;
        }
        const key = this.buildLotColumnKey(instrumentId, lot);
        if (meta[key]) {
          return;
        }
        meta[key] = { lot, instrumentId };
        columns.push(key);
      });
    });

    columns.sort((a, b) => {
      const instrumentCompare = meta[a].instrumentId.localeCompare(meta[b].instrumentId, undefined, { numeric: true });
      return instrumentCompare !== 0 ? instrumentCompare : meta[a].lot.localeCompare(meta[b].lot, undefined, { numeric: true });
    });

    return { columns, meta };
  }

  navigateToPrintView(): void {
    this.router.navigateByUrl('/(print:print/preview-run-setup)');
  }

  handleSelectColumns(selectedColumns: ColumnSelection[]): void {
    this.selectedColumns = selectedColumns;
    const selectedKeys = new Set(
      selectedColumns.filter(column => column.isSelected).map(column => column.columnKey ?? column.columnName)
    );
    const nextColumns = this.runSamplesColumns.filter(column => selectedKeys.has(column));
    this.displayedRunSamplesColumns = nextColumns.length ? nextColumns : [...this.runSamplesColumns];
  }

  /** Identifies the document in the print preview bar, beside the study name. */
  get printContextLine(): string {
    return [
      this.panelName,
      this.selectedTestSpec ? `Test Spec ${this.selectedTestSpec}` : '',
      this.specVersion ? `Spec Rev ${this.specVersion}` : ''
    ].filter(Boolean).join(' · ');
  }

  private syncPrintColumns(): void {
    const previousSelection = new Map(
      this.selectedColumns.map(column => [column.columnKey ?? column.columnName, column.isSelected])
    );
    this.tableColumns = this.runSamplesColumns.map(column => {
      const lotMeta = this.reagentLotColumnMeta[column];
      return {
        columnName: lotMeta ? `${lotMeta.instrumentId} · ${lotMeta.lot}` : column,
        columnKey: column,
        isSelected: previousSelection.get(column) ?? true,
        ...(lotMeta ? { kitLot: lotMeta.lot, instrumentId: lotMeta.instrumentId } : {})
      };
    });
    this.selectedColumns = this.tableColumns;
    this.displayedRunSamplesColumns = this.runSamplesColumns.filter(
      column => this.tableColumns.find(item => (item.columnKey ?? item.columnName) === column)?.isSelected
    );
  }

  private formatReagentLotLabel(lot: string): string {
    if (lot === undefined || lot === null) {
      return '';
    }
    const numeric = Number(lot);
    if (!Number.isNaN(numeric)) {
      return Math.trunc(numeric).toString();
    }
    return lot.trim();
  }

  private buildReagentLotValueMap(metadata: FileMetaData[]): Record<string, Record<string, number>> {
    const lotCounts: Record<string, Record<string, number>> = {};

    metadata.forEach(file => {
      const accessions = file.accessions || [];
      const replicateCounts = file.replicateCounts || {};
      const instrumentId = (file.instrumentId ?? '').toString().trim() || 'Unmapped';

      Object.entries(replicateCounts).forEach(([lotKey, values]) => {
        const formattedLot = this.formatReagentLotLabel(lotKey);
        if (!formattedLot) {
          return;
        }

        // Keyed by instrument+lot rather than lot alone, so the same lot run on two instruments
        // gets its own column instead of having their replicate counts added together.
        const columnKey = this.buildLotColumnKey(instrumentId, formattedLot);
        if (!lotCounts[columnKey]) {
          lotCounts[columnKey] = {};
        }

        (values || []).forEach((value, index) => {
          const accession = accessions[index];
          if (!accession) {
            return;
          }

          const numericValue = Number(value);
          if (!Number.isFinite(numericValue)) {
            return;
          }

          const normalizedAccession = accession.trim().toUpperCase();
          const currentValue = lotCounts[columnKey][normalizedAccession] ?? 0;
          lotCounts[columnKey][normalizedAccession] = currentValue + Math.trunc(numericValue);
        });
      });
    });

    return lotCounts;
  }

  /**
   * Keys mat-table rows by accessionId rather than object identity. storeFormValues() round-trips
   * edited rows through the store as brand-new Sample objects, so without a stable trackBy the table
   * would see every row as removed-and-re-added on each save and recreate its inputs, dropping focus
   * out of whichever cell the operator was typing in.
   */
  trackRunSample = (_index: number, sample: Sample): string => sample.accessionId;

  private getRunSampleAccession(rowIndex: number): string | null {
    const absoluteIndex = rowIndex + (this.pageIndex * this.pageSize);
    return this.runSamples?.[absoluteIndex]?.accessionId ?? null;
  }

  private getRunSampleFormControlValue(
    rowIndex: number,
    controlName: 'sampleId' | 'sampleType' | 'reqNumReplicates'
  ): unknown {
    if (!this.runSamplesFormArray) {
      return null;
    }
    const absoluteIndex = rowIndex + (this.pageIndex * this.pageSize);
    const formGroup = this.runSamplesFormArray.at(absoluteIndex);
    return formGroup?.get(controlName)?.value;
  }

  getRunSamplePrintSampleId(element: Sample, rowIndex: number): string {
    const value = this.getRunSampleFormControlValue(rowIndex, 'sampleId');
    return String(value ?? element.sampleId ?? '');
  }

  getRunSamplePrintSampleType(element: Sample, rowIndex: number): string {
    const value = this.getRunSampleFormControlValue(rowIndex, 'sampleType');
    return String(value ?? element.sampleType ?? '');
  }

  getRunSamplePrintReplicates(element: Sample, rowIndex: number): string {
    const value = this.getRunSampleFormControlValue(rowIndex, 'reqNumReplicates');
    return String(value ?? element.reqNumReplicates ?? '');
  }

  getReagentLotValue(column: string, rowIndex: number): string {
    const lotMap = this.reagentLotValues[column];
    if (!lotMap) {
      return '';
    }

    const accession = this.getRunSampleAccession(rowIndex)?.trim().toUpperCase();
    if (!accession) {
      return '';
    }

    const value = lotMap[accession];

    if (value === undefined || value === null || Number.isNaN(value)) {
      return '0';
    }

    return value.toString();
  }

  private getRunSampleReplicateCount(rowIndex: number): number | null {
    const formArray = this.runSetupFormGroup?.get('runSamples') as FormArray<FormGroup> | null;
    if (!formArray) {
      return null;
    }

    const absoluteIndex = rowIndex + (this.pageIndex * this.pageSize);
    const formGroup = formArray.at(absoluteIndex);
    if (!formGroup) {
      return null;
    }

    const value = formGroup.get('reqNumReplicates')?.value;
    if (value === undefined || value === null || value === '') {
      return null;
    }

    const numericValue = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  isReagentLotUnderrun(column: string, rowIndex: number): boolean {
    const replicateLimit = this.getRunSampleReplicateCount(rowIndex);
    if (replicateLimit === null) {
      return false;
    }

    const accession = this.getRunSampleAccession(rowIndex)?.trim().toUpperCase();
    if (!accession) {
      return false;
    }

    const lotValue = this.reagentLotValues[column]?.[accession];

    if (lotValue === undefined || lotValue === null || Number.isNaN(lotValue)) {
      return replicateLimit > 0;
    }

    return lotValue < replicateLimit;
  }

  private isCalibratorAssignmentOrAdjustmentRunType(): boolean {
    const normalizedRunType = (this.selectedStudyData?.runType ?? '').trim().replace(/[\s_-]+/g, '').toLowerCase();
    return normalizedRunType === 'calibratorassignment' || normalizedRunType === 'calibratoradjustment';
  }

  shouldShowSetupAdjustmentButton(): boolean {
    return this.isCalibratorAssignmentOrAdjustmentRunType();
  }

  handleSetupAdjustment(): void {
    if (!this.selectedStudyData) {
      return;
    }

    this.applyAdjustmentSampleTypeOverrides();

    const normalizedRunType = (this.selectedStudyData.runType ?? '').trim().replace(/[\s_-]+/g, '').toLowerCase();
    if (normalizedRunType !== 'calibratoradjustment') {
      const reportContent = this.getReportContentFromStore();
      // Vitamin D and HIV panels, and the instrument QC beads (SVB) on any panel, default to no optimization.
      const panel = (this.panelName ?? '').toLowerCase();
      const panelSkipsOptimization = panel.includes('vitamin d') || panel.includes('hiv');
      const powellContent: ReportContentItemResponse = {
        ...reportContent,
        reportContentItems: reportContent.reportContentItems.map(item => ({
          ...item,
          runValueOptimization: panelSkipsOptimization || isInternalQcBeadRegion(item.region) ? 'N/A' : 'powell'
        }))
      };
      this.store.dispatch(setPanel({
        panelName: this.panelName,
        runType: 'Calibrator Adjustment',
        panelSpecs: this.selectedStudyData.panelSpecs ?? null,
        reportContentItems: powellContent,
        assayNames: this.selectedStudyData.assayNames ?? [],
        beadRegions: this.selectedStudyData.beadRegions ?? []
      }));
    }

    this.markDirty();
  }

  private applyAdjustmentSampleTypeOverrides(): void {
    if (!this.runSamplesFormArray?.length) {
      return;
    }

    const validVialNumbers = this.getValidCalLotVialNumbers();
    const hasValidVials = validVialNumbers.size > 0;

    this.runSamplesFormArray.controls.forEach(formGroup => {
      const current = (formGroup.get('sampleType')?.value ?? '').toString();
      let next = current;
      const levelNumber = this.parseSampleIdLevel(formGroup.get('sampleId')?.value);

      if (hasValidVials && levelNumber !== null && !validVialNumbers.has(levelNumber)) {
        next = 'N/A';
      } else if (current === 'Cal - Test') {
        next = 'Cal - Cal';
      } else if (current === 'Cal - Cal') {
        next = 'N/A';
      }
      if (next !== current) {
        formGroup.get('sampleType')?.patchValue(next, { emitEvent: false });
      }
    });
  }

  private getValidCalLotVialNumbers(): Set<number> {
    const calLot = this.selectedStudyData?.calLots?.[0];
    if (!calLot?.vials?.length) {
      return new Set();
    }
    const numbers = calLot.vials
      .map(vial => vial.vialNumber)
      .filter((value): value is number => Number.isFinite(value));
    return new Set(numbers);
  }

  private parseSampleIdLevel(sampleId: string | null | undefined): number | null {
    if (!sampleId) {
      return null;
    }
    const match = /L(\d+)/i.exec(sampleId.trim());
    if (!match) {
      return null;
    }
    const value = Number(match[1]);
    return Number.isFinite(value) ? value : null;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.tableResizeObserver?.disconnect();
    this.setNavContainerOverride(false);
    this.cancelScheduledTabActivation();
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    // *MS Stop the throttled valueChanges listener so we do not leak subscriptions across component lifetimes.*MS
    this.runSamplesValueChangesSubscription?.unsubscribe();
    this.cdr.reattach();

    if (!this.isUpdated) {
      // *MS Persist data only when unsaved edits remain to avoid redundant serialization.*MS
      this.storeFormValues();
    }
  }

  private setNavContainerOverride(enabled: boolean): void {
    if (!this.navContainer) {
      this.navContainer = this.document.querySelector('.nav-container');
    }
    if (!this.navContainer) {
      return;
    }
    if (enabled) {
      this.renderer.addClass(this.navContainer, this.navContainerClass);
    } else {
      this.renderer.removeClass(this.navContainer, this.navContainerClass);
    }
  }

  private scheduleTabActivation(): void {
    // *MS Delay the heavy hydration work until the tab animation completes so only the visible tab does the rebuild.*MS
    this.cancelScheduledTabActivation();
    const activate = () => {
      this.tabActivationHandle = null;
      this.tabActivationHandleIsTimeout = false;
      this.isTabActive = true;
      this.cdr.reattach();
      this.cdr.markForCheck();
      const studyToLog = this.pendingSelectedStudy ?? this.selectedStudyData;
      if (this.pendingSelectedStudy) {
        this.applySelectedStudy(this.pendingSelectedStudy);
        this.pendingSelectedStudy = null;
      }
      this.logRunSetupDiagnostics(studyToLog);
    };

    if (this.shouldSkipAnimationDelay()) {
      activate();
      return;
    }

    if (typeof requestAnimationFrame === 'function') {
      this.tabActivationHandleIsTimeout = false;
      this.tabActivationHandle = requestAnimationFrame(activate);
      return;
    }

    this.tabActivationHandleIsTimeout = true;
    this.tabActivationHandle = setTimeout(activate, 0);
  }

  private cancelScheduledTabActivation(): void {
    // *MS Guard against queued frames when the user bounces between tabs so we never rehydrate while hidden.*MS
    if (this.tabActivationHandle === null) {
      return;
    }

    if (this.tabActivationHandleIsTimeout) {
      clearTimeout(this.tabActivationHandle as ReturnType<typeof setTimeout>);
    } else if (typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.tabActivationHandle as number);
    }

    this.tabActivationHandle = null;
  }

  private shouldSkipAnimationDelay(): boolean {
    // *MS Unit tests run under Karma and need synchronous activation, so bypass the animation delay in that environment.*MS
    if (typeof window === 'undefined') {
      return false;
    }
    const win = window as typeof window & { __karma__?: unknown };
    return Boolean(win.__karma__);
  }

  private logRunSetupDiagnostics(study: Study | null | undefined): void {
    const patientFileMetadataLength = study?.patientFileMetadata?.length ?? 0;
    const reportContentItemsLength = study?.reportContentItems?.reportContentItems?.length ?? 0;
    const runSamplesLength = study?.runSamples?.length ?? 0;
    const calLotsLength = study?.calLots?.length ?? 0;
    const qcLotsLength = study?.qcLots?.length ?? 0;
    const panelNameLength = this.panelName?.length ?? 0;

    console.log('[Run Setup] calculate gating lengths', {
      patientFileMetadata: patientFileMetadataLength,
      reportContentItems: reportContentItemsLength,
      runSamples: runSamplesLength,
      calLots: calLotsLength,
      qcLots: qcLotsLength,
      panelName: panelNameLength,
      isProcessing: this.isProcessing
    });
  }

  private scheduleDeferredSave(saveFn: () => void): void {
    const run = () => {
      if (this.destroyed) {
        return;
      }
      saveFn();
    };

    if (typeof queueMicrotask === 'function') {
      queueMicrotask(run);
      return;
    }

    Promise.resolve().then(run);
  }

  private applySelectedStudy(selectedStudy: Study): void {
    this.selectedStudyData = selectedStudy;
    this.studyName = selectedStudy?.studyName ?? '';
    this.panelName = this.panelName || selectedStudy?.panelSpecs?.kitname || '';
    this.specVersion = selectedStudy?.panelSpecs?.revisionNumber ?? '';
    // *MS Seed the cache immediately when a fresh study arrives so initial saves still include the correct report items.*MS
    this.latestReportContentResponse = selectedStudy.reportContentItems ?? null;
    this.updateReagentLotColumns(selectedStudy);
    this.calculationResult = selectedStudy?.calculationResponse?.calculationResponse;
    this.missingSamples = this.getUniqueAccessionNumbersFromSamples();
    this.missingDataSet();
    this.phaseType = selectedStudy.runType;
    this.replicatesToRun = selectedStudy?.panelSpecs?.numReplicatesToRun;

    this.isLoading = true;
    this.selectedTestSpec = this.resolveTestSpec(selectedStudy);
    const assignSpec = selectedStudy.assignSpec;
    this.selectedAssignSpec = assignSpec && assignSpec.trim().length ? assignSpec : 'Final';
    this.selectedKitLot = selectedStudy.refKitLot;
    this.calculateAverages = !!selectedStudy.calculate_averages;
    this.useLegacy4pl = !!selectedStudy.use_legacy_4pl;
    this.optimizeAdjustment = !!selectedStudy.optimize_adjustment;
    this.skipMissingSamples = !!selectedStudy.skip_missing_samples;
    this.kitLots = selectedStudy.kitLots.map(f => String(f)) ?? [];
    this.calLotData = selectedStudy.calLots;
    this.qcLotData = selectedStudy.qcLots;
    this.studyComment = selectedStudy.comments ?? '';
    this.lastKnownComment = this.studyComment;
    this.loadTables(selectedStudy);
  }
}
